import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, renderSlot as _renderSlot, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "tabs" }
const _hoisted_2 = ["aria-label"]
const _hoisted_3 = ["id", "aria-selected", "aria-controls", "tabindex", "disabled", "onClick", "onKeydown"]
const _hoisted_4 = ["id", "aria-labelledby"]

import { computed, ref, useId, watch } from "vue";
import type { ComponentPublicInstance } from "vue";

const keyArrowLeft = "ArrowLeft";
const keyArrowRight = "ArrowRight";
const keyHome = "Home";
const keyEnd = "End";

export default /*@__PURE__*/_defineComponent({
  __name: 'Tabs',
  props: {
    tabs: {},
    modelValue: {},
    ariaLabel: {}
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props;

const emit = __emit;

const generatedId = useId();
const tabButtonElements = ref<(HTMLButtonElement | null)[]>([]);
const navigationKeys = [keyArrowLeft, keyArrowRight, keyHome, keyEnd];

const firstEnabledTabId = computed(() => props.tabs.find((tab) => !tab.disabled)?.id ?? "");
const activeTabId = ref(getInitialTabId());

watch(
    () => props.modelValue,
    (newValue) => {
      if (isSelectableTab(newValue)) {
        activeTabId.value = newValue;
        return;
      }

      ensureValidActiveTab();
    },
);

watch(
    () => props.tabs,
    () => {
      tabButtonElements.value = [];
      ensureValidActiveTab();
    },
    {deep: true},
);

function getInitialTabId(): string {
  if (isSelectableTab(props.modelValue)) {
    return props.modelValue;
  }

  return firstEnabledTabId.value;
}

function isSelectableTab(tabId?: string): tabId is string {
  return Boolean(tabId) && props.tabs.some((tab) => tab.id === tabId && !tab.disabled);
}

function ensureValidActiveTab(): void {
  if (isSelectableTab(activeTabId.value)) {
    return;
  }

  const nextTabId = firstEnabledTabId.value;
  activeTabId.value = nextTabId;

  if (nextTabId && props.modelValue !== nextTabId) {
    emit("update:modelValue", nextTabId);
  }
}

function setActiveTab(tabId: string): void {
  if (!isSelectableTab(tabId) || activeTabId.value === tabId) {
    return;
  }

  activeTabId.value = tabId;
  emit("update:modelValue", tabId);
}

function setTabButtonRef(element: Element | ComponentPublicInstance | null, index: number): void {
  tabButtonElements.value[index] = element instanceof HTMLButtonElement ? element : null;
}

function onTabKeydown(event: KeyboardEvent, currentIndex: number): void {
  if (!navigationKeys.includes(event.key)) {
    return;
  }

  event.preventDefault();

  const enabledIndexes = props.tabs
      .map((tab, index) => ({index, disabled: tab.disabled === true}))
      .filter((item) => !item.disabled)
      .map((item) => item.index);

  if (!enabledIndexes.length) {
    return;
  }

  const enabledPosition = enabledIndexes.indexOf(currentIndex);
  let nextIndex = currentIndex;

  if (event.key === keyHome) {
    nextIndex = enabledIndexes[0];
  } else if (event.key === keyEnd) {
    nextIndex = enabledIndexes[enabledIndexes.length - 1];
  } else if (enabledPosition >= 0) {
    const direction = event.key === keyArrowRight ? 1 : -1;
    const nextPosition = (enabledPosition + direction + enabledIndexes.length) % enabledIndexes.length;
    nextIndex = enabledIndexes[nextPosition];
  }

  const nextTab = props.tabs[nextIndex];
  if (!nextTab) {
    return;
  }

  setActiveTab(nextTab.id);
  tabButtonElements.value[nextIndex]?.focus();
}

function getTabId(tabId: string): string {
  return `${generatedId}-${tabId}-tab`;
}

function getPanelId(tabId: string): string {
  return `${generatedId}-${tabId}-panel`;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "tabs__list",
      role: "tablist",
      "aria-label": __props.ariaLabel
    }, [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.tabs, (tab, index) => {
        return (_openBlock(), _createElementBlock("button", {
          id: getTabId(tab.id),
          key: tab.id,
          ref_for: true,
          ref: (element) => setTabButtonRef(element, index),
          class: _normalizeClass(["button button--large", { 'button--inverted': activeTabId.value !== tab.id }]),
          role: "tab",
          type: "button",
          "aria-selected": activeTabId.value === tab.id,
          "aria-controls": getPanelId(tab.id),
          tabindex: activeTabId.value === tab.id ? 0 : -1,
          disabled: tab.disabled,
          onClick: ($event: any) => (setActiveTab(tab.id)),
          onKeydown: ($event: any) => (onTabKeydown($event, index))
        }, _toDisplayString(tab.label), 43 /* TEXT, CLASS, PROPS, NEED_HYDRATION */, _hoisted_3))
      }), 128 /* KEYED_FRAGMENT */))
    ], 8 /* PROPS */, _hoisted_2),
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(__props.tabs, (tab) => {
      return _withDirectives((_openBlock(), _createElementBlock("div", {
        id: getPanelId(tab.id),
        key: tab.id,
        class: "tabs__panel",
        role: "tabpanel",
        "aria-labelledby": getTabId(tab.id)
      }, [
        _renderSlot(_ctx.$slots, tab.id, { tab: tab })
      ], 8 /* PROPS */, _hoisted_4)), [
        [_vShow, activeTabId.value === tab.id]
      ])
    }), 128 /* KEYED_FRAGMENT */))
  ]))
}
}

})